import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { Wallet, Search, UserPlus, Trash2 } from 'lucide-react';

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ currency: '', amount: '', action: 'credit', reason: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', username: '', name: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (!createForm.email || !createForm.password) { toast.error('Email and password required'); return; }
    setCreating(true);
    try {
      await adminAPI.createUser(createForm);
      toast.success('User created');
      setShowCreate(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
    finally { setCreating(false); }
  };

  const deleteUser = async (u) => {
    const displayName = u.username || u.name || `User ${u.id?.substring(0, 8)}`;
    if (!window.confirm(`Permanently delete ${displayName}? Their wallets, contacts and follows will be removed. This cannot be undone.`)) return;
    try {
      await adminAPI.deleteUser(u.id);
      toast.success('User deleted');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const promoteToAgent = async (u) => {
    const displayName = u.username || u.name || `User ${u.id?.substring(0, 8)}`;
    if (!window.confirm(`Promote ${displayName} to Agent role? They will get access to the Agent Portal and can process cash deposits/withdrawals.`)) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${u.id}/promote-to-agent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        toast.success(`${displayName} promoted to Agent!`);
        load();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to promote user');
      }
    } catch (e) {
      toast.error('Failed to promote user');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, cRes] = await Promise.all([
        adminAPI.getUsers(0, 200),
        publicAPI.getCurrencies(true)
      ]);
      setUsers(uRes.data);
      setCurrencies(cRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const openAdjust = (user) => {    setSelected(user);
    setAdjustForm({ currency: currencies[0]?.symbol || '', amount: '', action: 'credit', reason: '' });
  };

  const submitAdjust = async () => {
    if (!adjustForm.amount || !adjustForm.reason) {
      toast.error('Amount and reason are required');
      return;
    }
    try {
      await adminAPI.adjustWallet(
        selected.id,
        adjustForm.currency,
        parseFloat(adjustForm.amount),
        adjustForm.action,
        adjustForm.reason
      );
      toast.success(`Wallet ${adjustForm.action}ed successfully`);
      setSelected(null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed');
    }
  };

  const filtered = users.filter(u => 
    !search || 
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Users</h1>
          <p className="mt-2 text-muted-foreground">Manage platform users and their wallets</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle>All Users ({filtered.length})</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-9 w-64"
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="user-search"
                  />
                </div>
                <Button onClick={() => { setCreateForm({ email: '', password: '', username: '', name: '' }); setShowCreate(true); }} data-testid="create-user-btn">
                  <UserPlus className="h-4 w-4 mr-2" /> New user
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username / Name</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <span className="font-semibold">{u.username || u.name || 'No username'}</span>
                            {u.verified && <span title="Verified" className="text-blue-600">✓</span>}
                          </div>
                          {u.name && u.username && u.name !== u.username && (
                            <span className="text-xs text-muted-foreground">{u.name}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">{u.id?.substring(0, 12)}...</TableCell>
                      <TableCell className="capitalize">{u.role?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-sm">{formatDate(u.created_at)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant={u.verified ? "outline" : "ghost"} onClick={async () => {
                          try {
                            await adminAPI.verifyUser(u.id, !u.verified);
                            toast.success(u.verified ? 'Unverified' : 'Verified!');
                            load();
                          } catch (e) { toast.error('Failed'); }
                        }} data-testid={`verify-${u.id}`} title="Toggle verification badge">
                          {u.verified ? '✓ Verified' : 'Verify'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAdjust(u)} data-testid={`adjust-wallet-${u.id}`}>
                          <Wallet className="h-3 w-3 mr-1" /> Adjust
                        </Button>
                        {u.role === 'customer' && (
                          <Button size="sm" variant="default" onClick={() => promoteToAgent(u)} data-testid={`promote-agent-${u.id}`} title="Promote to Agent" className="bg-indigo-600 hover:bg-indigo-700">
                            Agent
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`} title="Delete account">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adjust Wallet - {selected?.username || selected?.name || 'User'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={adjustForm.action} onValueChange={(v) => setAdjustForm({...adjustForm, action: v})}>
                  <SelectTrigger data-testid="adjust-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit (Add funds)</SelectItem>
                    <SelectItem value="debit">Debit (Remove funds)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={adjustForm.currency} onValueChange={(v) => setAdjustForm({...adjustForm, currency: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number" step="0.01"
                  value={adjustForm.amount}
                  onChange={(e) => setAdjustForm({...adjustForm, amount: e.target.value})}
                  data-testid="adjust-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Input
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({...adjustForm, reason: e.target.value})}
                  placeholder="Required for audit log"
                  data-testid="adjust-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
              <Button onClick={submitAdjust} data-testid="submit-adjust-wallet">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create user dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle>Create user account</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Email *</Label>
                <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="create-user-email" />
              </div>
              <div className="space-y-1">
                <Label>Password *</Label>
                <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Min 6 characters" data-testid="create-user-password" />
              </div>
              <div className="space-y-1">
                <Label>Username (optional)</Label>
                <Input value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} data-testid="create-user-username" />
              </div>
              <div className="space-y-1">
                <Label>Display name (optional)</Label>
                <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="create-user-name" />
              </div>
              <p className="text-xs text-muted-foreground">The user will be created verified-off. They can log in immediately with the credentials you set.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={createUser} disabled={creating} data-testid="submit-create-user">
                <UserPlus className="h-4 w-4 mr-2" /> {creating ? 'Creating…' : 'Create user'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
